/**
 * Store de estado para UI (interfaz de usuario)
 * @module stores/useUIStore
 */

import { create } from 'zustand'
import { persist } from 'zustand/middleware'

// ============================================================================
// TYPES
// ============================================================================

export type ThemeMode = 'light' | 'dark'
export type MapView = 'street' | 'satellite' | 'terrain'
export type MapSelectMode = 'navigate' | 'rect'

interface UIStore {
  // Tema
  theme: ThemeMode
  toggleTheme: () => void
  setTheme: (theme: ThemeMode) => void

  // Paneles
  sidebarOpen: boolean
  detailPanelOpen: boolean
  tracePanelOpen: boolean
  importExportModalOpen: boolean
  tenantAdminModalOpen: boolean
  platformAdminModalOpen: boolean
  toggleSidebar: () => void
  toggleDetailPanel: () => void
  toggleTracePanel: () => void
  toggleImportExportModal: () => void
  setSidebarOpen: (open: boolean) => void
  setDetailPanelOpen: (open: boolean) => void
  setTracePanelOpen: (open: boolean) => void
  setImportExportModalOpen: (open: boolean) => void
  setTenantAdminModalOpen: (open: boolean) => void
  setPlatformAdminModalOpen: (open: boolean) => void

  // Mapa
  mapView: MapView
  setMapView: (view: MapView) => void
  nodeMarkerSize: number
  setNodeMarkerSize: (size: number) => void
  showLabels: boolean
  toggleLabels: () => void
  showCables: boolean
  toggleCables: () => void

  // Filtros
  visibleNodeTypes: Set<string>
  toggleNodeTypeVisibility: (type: string) => void
  setAllNodeTypesVisible: (visible: boolean) => void

  // Modo de trabajo
  mode: 'view' | 'addNode' | 'addCable' | 'edit' | 'editCableRoute'
  setMode: (mode: UIStore['mode']) => void

  // Edición de ruta de cable existente
  editingCableId: string | null
  setEditingCableId: (id: string | null) => void

  // Modo edición de posición (mover nodos)
  editModeEnabled: boolean
  toggleEditMode: () => void
  setEditModeEnabled: (enabled: boolean) => void

  /** Selección múltiple en mapa (rectángulo + Ctrl+clic) */
  mapSelectMode: MapSelectMode
  setMapSelectMode: (mode: MapSelectMode) => void
  selectedNodeIds: string[]
  setMapSelection: (ids: string[]) => void
  addToMapSelection: (ids: string[]) => void
  toggleNodeInMapSelection: (id: string) => void
  clearMapSelection: () => void

  // Notificaciones
  notifications: Array<{
    id: string
    type: 'info' | 'success' | 'warning' | 'error'
    message: string
    timestamp: Date
  }>
  addNotification: (
    type: 'info' | 'success' | 'warning' | 'error',
    message: string
  ) => void
  removeNotification: (id: string) => void
  clearNotifications: () => void
}

// ============================================================================
// STORE
// ============================================================================

export const useUIStore = create<UIStore>()(
  persist(
    (set, get) => ({
      // Estado inicial - Tema
      theme: 'light',
      toggleTheme: () => {
        set((state: UIStore) => ({ theme: state.theme === 'light' ? 'dark' : 'light' }))
      },
      setTheme: (theme: ThemeMode) => set({ theme }),

      // Estado inicial - Paneles
      sidebarOpen: true,
      detailPanelOpen: false,
      tracePanelOpen: false,
      importExportModalOpen: false,
      tenantAdminModalOpen: false,
      platformAdminModalOpen: false,
      toggleSidebar: () => set((state: UIStore) => ({ sidebarOpen: !state.sidebarOpen })),
      toggleDetailPanel: () =>
        set((state: UIStore) => ({ detailPanelOpen: !state.detailPanelOpen })),
      toggleTracePanel: () =>
        set((state: UIStore) => ({ tracePanelOpen: !state.tracePanelOpen })),
      toggleImportExportModal: () =>
        set((state: UIStore) => ({ importExportModalOpen: !state.importExportModalOpen })),
      setSidebarOpen: (open: boolean) => set({ sidebarOpen: open }),
      setDetailPanelOpen: (open: boolean) => set({ detailPanelOpen: open }),
      setTracePanelOpen: (open: boolean) => set({ tracePanelOpen: open }),
      setImportExportModalOpen: (open: boolean) => set({ importExportModalOpen: open }),
      setTenantAdminModalOpen: (open: boolean) => set({ tenantAdminModalOpen: open }),
      setPlatformAdminModalOpen: (open: boolean) => set({ platformAdminModalOpen: open }),

      // Estado inicial - Mapa
      mapView: 'street',
      setMapView: (view: MapView) => set({ mapView: view }),
      nodeMarkerSize: 20,
      setNodeMarkerSize: (size: number) => set({ nodeMarkerSize: size }),
      showLabels: true,
      toggleLabels: () => set((state: UIStore) => ({ showLabels: !state.showLabels })),
      showCables: true,
      toggleCables: () => set((state: UIStore) => ({ showCables: !state.showCables })),

      // Estado inicial - Filtros
      visibleNodeTypes: new Set(['OLT', 'NAP', 'CIERRE', 'CLIENTE', 'POSTE', 'POP']),
      toggleNodeTypeVisibility: (type: string) => {
        set((state: UIStore) => {
          const newSet = new Set(state.visibleNodeTypes)
          if (newSet.has(type)) {
            newSet.delete(type)
          } else {
            newSet.add(type)
          }
          return { visibleNodeTypes: newSet }
        })
      },
      setAllNodeTypesVisible: (visible: boolean) => {
        if (visible) {
          set({ visibleNodeTypes: new Set(['OLT', 'NAP', 'CIERRE', 'CLIENTE', 'POSTE', 'POP']) })
        } else {
          set({ visibleNodeTypes: new Set() })
        }
      },

      // Estado inicial - Modo
      mode: 'view',
      setMode: (mode: UIStore['mode']) => set({ mode }),

      // Edición de ruta de cable existente
      editingCableId: null,
      setEditingCableId: (id: string | null) => set({ editingCableId: id }),

      // Estado inicial - Modo edición de posición
      editModeEnabled: false,
      toggleEditMode: () => set((state: UIStore) => ({ editModeEnabled: !state.editModeEnabled })),
      setEditModeEnabled: (enabled: boolean) => set({ editModeEnabled: enabled }),

      mapSelectMode: 'navigate',
      setMapSelectMode: (mode: MapSelectMode) => set({ mapSelectMode: mode }),
      selectedNodeIds: [],
      setMapSelection: (ids: string[]) => set({ selectedNodeIds: [...new Set(ids)] }),
      addToMapSelection: (ids: string[]) =>
        set((state: UIStore) => ({
          selectedNodeIds: [...new Set([...state.selectedNodeIds, ...ids])],
        })),
      toggleNodeInMapSelection: (id: string) =>
        set((state: UIStore) => {
          const next = new Set(state.selectedNodeIds)
          if (next.has(id)) next.delete(id)
          else next.add(id)
          return { selectedNodeIds: [...next] }
        }),
      clearMapSelection: () => set({ selectedNodeIds: [] }),

      // Estado inicial - Notificaciones
      notifications: [],
      addNotification: (type: 'success' | 'error' | 'warning' | 'info', message: string) => {
        const notification = {
          id: `notif-${Date.now()}`,
          type,
          message,
          timestamp: new Date(),
        }
        set((state: UIStore) => ({
          notifications: [...state.notifications, notification],
        }))

        // Auto-remover después de 5 segundos
        setTimeout(() => {
          get().removeNotification(notification.id)
        }, 5000)
      },
      removeNotification: (id: string) => {
        set((state: UIStore) => ({
          notifications: state.notifications.filter((n: any) => n.id !== id),
        }))
      },
      clearNotifications: () => set({ notifications: [] }),
    }),
    {
      name: 'neteasyftx-ui-store',
      partialize: (state) => ({
        theme: state.theme,
        sidebarOpen: state.sidebarOpen,
        mapView: state.mapView,
        nodeMarkerSize: state.nodeMarkerSize,
        showLabels: state.showLabels,
        showCables: state.showCables,
        visibleNodeTypes: Array.from(state.visibleNodeTypes),
      }),
      merge: (persistedState: any, currentState: any) => ({
        ...currentState,
        ...persistedState,
        // Convertir Array a Set al cargar desde localStorage
        visibleNodeTypes: Array.isArray(persistedState?.visibleNodeTypes)
          ? new Set(persistedState.visibleNodeTypes)
          : currentState.visibleNodeTypes,
      }),
    }
  )
)
